import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, analytics } from "../firebaseConfig";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useLanguage } from "../i18n/LanguageContext";
import { logEvent } from "firebase/analytics";




import "../styles/generalStyle.css";
import "../styles/entitlements.css";
import "./Paywall.css";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function snippet2Lines(text = "") {
  const s = String(text || "").trim();
  if (!s) return "";
  const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length >= 2) return `${lines[0]}\n${lines[1]}`;
  if (lines.length === 1) return lines[0];
  // fallback por si no hay saltos
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
}


export default function Paywall() {
  const navigate = useNavigate();
  const q = useQuery();
  const { t, lang } = useLanguage();

  const preSongId = q.get("songId") || "";


  const [uid, setUid] = useState(null);
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  // canciones pendientes
  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(true);
  const [selectedSongId, setSelectedSongId] = useState("");

  // ✅ monedas disponibles (porque ya tienes precios USD y EUR)
  const currencies = ["USD", "EUR", "BRL"];

  // default por idioma
  const defaultCurrency = useMemo(() => {
    // de/fr -> EUR, lo demás -> USD
    return ["de", "fr"].includes(lang) ? "EUR" : "USD";
  }, [lang]);

  // override manual (persistente)
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem("pay_currency") || "AUTO";
  });

  const effectiveCurrency = currency === "AUTO" ? defaultCurrency : currency;

  useEffect(() => {
    localStorage.setItem("pay_currency", currency);
  }, [currency]);


  const prices = { USD: 15, EUR: 15, BRL: 60 }; 
  const amount = prices[effectiveCurrency] ?? 15;

  const priceLabel = useMemo(() => {
    try {
      return new Intl.NumberFormat(lang || "en", {
        style: "currency",
        currency: effectiveCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      // fallback
      return effectiveCurrency === "EUR" ? `€${amount}` : `$${amount} USD`;
    }
  }, [amount, effectiveCurrency, lang]);


  // Retorno de Stripe
  const status = q.get("status"); // success | canceled | failed
  const reason = q.get("reason");
  const sessionId = q.get("session_id");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      setUid(user.uid);
      setEmail(user.email || "");
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  // Cargar canciones pendientes
  useEffect(() => {
    const loadSongs = async () => {
      if (!uid) return;
      setSongsLoading(true);
      try {
        const col = collection(db, "usuarios", uid, "canciones");
        const qq = query(col, orderBy("fechaCreacion", "desc"), limit(50));
        const snap = await getDocs(qq);
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Pendientes: pagoWeb !== true (incluye undefined)
        const pending = all.filter(s => s?.pagoWeb !== true);
        // auto-selección
        if (pending.length > 0) {
          const found = preSongId && pending.some(s => s.id === preSongId);
          setSelectedSongId(found ? preSongId : pending[0].id);
        }



        setSongs(pending);

        // auto-selección
        if (!selectedSongId && pending.length > 0) {
          setSelectedSongId(pending[0].id);
        }
      } catch (e) {
        console.error("loadSongs error:", e);
      } finally {
        setSongsLoading(false);
      }
    };

    loadSongs();
  }, [uid]); // no metas selectedSongId aquí para evitar loop

  const selectedSong = useMemo(
    () => songs.find(s => s.id === selectedSongId) || null,
    [songs, selectedSongId]
  );

  async function startCheckout() {
    if (!uid) return;
    if (!selectedSongId) {
      alert(t("pay.selectSongRequired", "Selecciona una canción para desbloquear."));
      return;
    }
    



    setStarting(true);
    try {
      // ✅ llama a tu Cloud Function HTTPS (NO metas SK en front)
      // Pon tu URL real aquí:
      const CREATE_CHECKOUT_URL = "https://us-central1-cancionesia.cloudfunctions.net/createCheckoutSessionWeb";

      const resp = await fetch(CREATE_CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          email,
          songId: selectedSongId,
          currency: effectiveCurrency,
       //   successUrl: `${window.location.origin}/app/pago?status=success`,
      //    cancelUrl: `${window.location.origin}/app/pago?status=canceled`,
          successUrl: `${window.location.origin}/app/pagos?status=success&songId=${selectedSongId}`,
          cancelUrl: `${window.location.origin}/app/pagos?status=canceled&songId=${selectedSongId}`,

        })
      });



      let data = {};
      try { data = await resp.json(); } catch {}

      if (!resp.ok) {
        throw new Error(data?.error || "No se pudo iniciar el pago.");
      }

      if (!data?.url) throw new Error("Stripe URL missing");
      window.location.href = data.url;

      if (analytics) logEvent(analytics, "inicioPago", { currency: effectiveCurrency, value: 15 });


      if (window.fbq) {
        window.fbq("track", "Purchase", {
          currency: "USD",
          value: 15,
          content_ids: [selectedSongId],
          content_type: "product",
        });
      }


    } catch (e) {
      console.error(e);
      alert(e?.message || t("pay.failedGeneric"));
    } finally {
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="ent-alert">{t("pay.loading")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container pay-wrap">
        <div className="pay-header">
          <h1 className="h1">{t("pay.title")}</h1>
          <p className="p-muted" style={{ marginTop: 8 }}>
            {t("pay.subtitle")}
          </p>
        </div>

        {/* Retorno */}
        {status === "success" && (
          <div className="pay-result success">
            <div className="pay-result-title">{t("pay.successTitle")}</div>
            <div className="pay-result-desc">
              {t("pay.successDesc")}
            </div>
            <div className="pay-result-actions">
              <button className="btn btn-primary" onClick={() => navigate("/app/songs")}>
                {t("pay.goSongs", "Ir a mis canciones")}
              </button>
              <button className="btn btn-primary" onClick={() => navigate(`/app/songs/${q.get("songId") || ""}`)}>
                {t("pay.goSong", "Ver mi canción")}
              </button>

            </div>
            {sessionId ? <div className="pay-result-mini">Session: {sessionId}</div> : null}
          </div>
        )}

        {status === "canceled" && (
          <div className="pay-result neutral">
            <div className="pay-result-title">{t("pay.canceledTitle")}</div>
            <div className="pay-result-desc">{t("pay.canceledDesc")}</div>
          </div>
        )}

        {status === "failed" && (
          <div className="pay-result error">
            <div className="pay-result-title">{t("pay.failedTitle")}</div>
            <div className="pay-result-desc">
              {t("pay.failedDesc")}
              {reason ? ` (${reason})` : ""}
            </div>
            <div className="pay-result-actions">
              <button className="btn btn-primary" onClick={startCheckout}>
                {t("pay.tryAgain")}
              </button>
            </div>
          </div>
        )}

        <div className="pay-card">
          <div className="pay-card-top">
            <div className="pay-price">
              <div className="pay-price-label">{t("pay.priceLabel")}</div>
              <div className="pay-price-value">{priceLabel}</div>

                <div className="pay-currencyRow" style={{ marginTop: 8 }}>
                  <label className="label" style={{ margin: 0 }}>
                    {t("pay.currency", "Moneda")}
                  </label>
                  <select
                    className="input"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{ marginTop: 6 }}
                  >
                    <option value="AUTO">{t("pay.currencyAuto", "Automática")}</option>
                    {currencies.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <div className="p-muted" style={{ marginTop: 6 }}>
                    {t("pay.currencyHint", "Puedes cambiar la moneda si lo prefieres.")}
                  </div>
                </div>

            </div>

             <div className="pay-includes">
              <div className="pay-includes-title">{t("pay.includesTitle")}</div>
              <ul className="pay-ul">
                <li>{t("pay.includes1")}</li>
                <li>{t("pay.includes2")}</li>
                <li>{t("pay.includes3")}</li>
                <li>{t("pay.includes4")}</li>
              </ul>
            </div>
          </div>

         
          <div className="pay-note">
            <div className="pay-note-title">{t("pay.noteTitle")}</div>
            <div className="pay-note-desc">{t("pay.noteDesc")}</div>
          </div>


          <div className="pay-note">
            <div className="pay-note-title">{t("pay.chooseSongTitle", "Elige la canción a desbloquear")}</div>

           {songsLoading ? (
            <div className="p-muted" style={{ marginTop: 8 }}>
              {t("pay.loadingSongs", "Cargando canciones...")}
            </div>
          ) : songs.length === 0 ? (
            <div className="p-muted" style={{ marginTop: 8 }}>
              {t("pay.noPendingSongs", "No tienes canciones pendientes por desbloquear.")}
            </div>
          ) : (
           <div className="pay-songListCompact">
            {songs.map((s, idx) => {
              const title = (s.titulo || "").trim() || t("songDetails.untitled", "Untitled");
              const preview = snippet2Lines(s.letra);
              const a = (s.coverUrl || "").trim();
              const b = (s.coverUrl02 || "").trim();

              return (
                <div key={s.id} className="pay-songItemWrap">
                  <label
                    className={`pay-songRowCompact ${selectedSongId === s.id ? "active" : ""} ${idx % 2 === 0 ? "zebraA" : "zebraB"}`}
                  >
                    <input
                      className="pay-radio"
                      type="radio"
                      name="songPick"
                      checked={selectedSongId === s.id}
                      onChange={() => setSelectedSongId(s.id)}
                    />

                    <div className="pay-songMain">
                      <div className="pay-songTitleRow">
                        <div className="pay-songTitle">{title}</div>

                        <div className="pay-badges">
                          <span className={`pay-badge ${a ? "" : "off"}`}>A</span>
                          <span className={`pay-badge ${b ? "" : "off"}`}>B</span>
                        </div>
                      </div>

                      {/* ✅ primeras 2 líneas */}
                      {preview ? <pre className="pay-songPreview2">{preview}</pre> : null}

                      {(a || b) ? (
                        <div className="pay-audiosCompact">
                          {a ? (
                            <audio controls preload="none" className="pay-audioCompact">
                              <source src={a} />
                            </audio>
                          ) : null}

                          {b ? (
                            <audio controls preload="none" className="pay-audioCompact">
                              <source src={b} />
                            </audio>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </label>

                  {/* ✅ separador real */}
                  {idx !== songs.length - 1 ? <div className="pay-sep" /> : null}
                </div>
              );
            })}
          </div>

          )}

          </div>

          {/* Botones */}
          <div className="pay-actions">
            <button
              className="btn btn-primary"
              onClick={startCheckout}
              disabled={starting || !selectedSongId || songs.length === 0}
            >
              {starting ? t("pay.processing") : t("pay.payButton")}
            </button>

            <button className="btn btn-ghost" onClick={() => navigate("/app/chat")}>
              {t("pay.chatInstead")}
            </button>
          </div>

          <div className="pay-footer">{t("pay.footerThanks")}</div>
        </div>
      </div>
    </div>
  );
}
